import React, { useEffect, useState } from 'react';
import {
  Button,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  FormText,
} from 'reactstrap';
import styles from './UserProfile.module.scss';
import getClientLocations from '../../utils/get-client-locations';
import LocationSelector from '../LocationSelector';
import FormValidator from '../../utils/FormValidator';
import validationConfig from './validationConfig';
import SanitizedTextInput from '../SanitizedTextInput';
import * as userService from '../../services/user-services';
import useUserAttributes from '../../hooks/useUserAttributes';
import cognitoUser from '../../utils/cognito-user';
import { mapPhoneNumberFromDisplay, mapPhoneNumberToDisplay } from '../../utils/phone-mapper';
import SendVerificationModal from '../SendVerificationModal';
import initialsFromCamelCase from '../../utils/initials-from-camel-case';
import { useModal } from '../../hooks/useModal';

function VerifiableFormGroup({
  label, inputProps, isVerified, onVerify, validationError,
}) {
  if (isVerified) {
    return (
      <FormGroup>
        <div className="d-flex justify-content-between">
          {label}
          <span className="text-primary mx-2">Verified</span>
        </div>
        <SanitizedTextInput
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...inputProps}
        />
        {validationError}
      </FormGroup>
    );
  }

  return (
    <FormGroup>
      {label}
      <div className="row w-100">
        <div className="col-11">
          <SanitizedTextInput
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...inputProps}
            className={`${inputProps.className ?? ''} ${styles.verifyField}`}
          />
        </div>
        <div className="col-1 ml-n4 pl-2">
          <button
            type="button"
            className={`btn btn-outline-danger h-100 pb-1 px-0 ${styles.verifyButton}`}
            onClick={onVerify}
            disabled={!inputProps?.value}
          >
            Verify
          </button>
        </div>
      </div>
      {validationError}
    </FormGroup>
  );
}

const onSave = async (
  setSaveWasSuccessful,
  setSaveFailed,
  setValidations,
  user,
  syncUserAttributesUpdates,
  setLoading,
) => {
  setLoading(true);
  setValidations({});
  const validator = new FormValidator(validationConfig);
  const results = validator.validate({
    firstName: user.given_name,
    lastName: user.family_name,
    phoneNumber: user.phone_number,
  });

  if (results.isInvalid) {
    setValidations(results);
    setLoading(false);
    return false;
  }
  try {
    const [cognitoResult, mfaResult] = await Promise.all([
      userService.updateCognitoAttributes({
        family_name: user.family_name,
        given_name: user.given_name,
        phone_number: user.phone_number ?? null,
      }),
      userService.updateMfaPreferences(user.mfaPreferences),
      syncUserAttributesUpdates(),
    ]);
    if (!cognitoResult.success) {
      throw new Error(cognitoResult.error);
    }
    if (!mfaResult.success) {
      throw new Error(mfaResult.error);
    }
    setSaveWasSuccessful(true);
  } catch (error) {
    console.error('Error updating user attributes', error);
    setSaveFailed(true);
    return false;
  } finally {
    setLoading(false);
  }
  return true;
};

const renderValidationError = (validation) => ((validation && validation.isInvalid) ? (<small className="validationError text-danger">{validation.message}</small>) : null);

function SuccessModal({ onClose }) {
  return (
    <Modal isOpen>
      <ModalHeader>Success</ModalHeader>
      <ModalBody>
        <div id="successMessage">
          Your user profile was successfully updated.
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          onClick={() => onClose()}
        >
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function MFACheckBox({
  enabled, toggle, toggleDisabled, disabledReason, verificationType,
}) {
  const { showModal, hideModal } = useModal();
  const id = `customSwitch-${verificationType}`;
  return (
    <div className="ml-2 mt-1 custom-control custom-switch align-middle">
      <input
        type="checkbox"
        className="custom-control-input"
        checked={enabled}
        disabled={toggleDisabled}
        id={id}
        onChange={(event) => {
          event.preventDefault();
          event.stopPropagation();
          showModal({
            header: 'Update MFA',
            body: `Are you sure you want to ${enabled ? 'disable' : 'enable'} ${verificationType} verification when you login?`,
            buttons: [
              {
                color: 'primary',
                text: 'Yes',
                onClick: () => {
                  toggle();
                  hideModal();
                },
              },
              {
                color: 'secondary',
                text: 'Cancel',
                onClick: () => {
                  hideModal();
                },
              },
            ],
          });
        }}
      />
      <label
        className="custom-control-label align-middle"
        htmlFor={id}
      >
        Enable
        {' '}
        {verificationType}
        {' '}
        {toggleDisabled && `(${disabledReason})`}
      </label>
    </div>
  );
}

function ErrorModal({ onClose }) {
  return (
    <Modal isOpen>
      <ModalHeader>Error Saving Profile</ModalHeader>
      <ModalBody>
        <div id="errorMessage">
          There was an error saving your account. Please try again later or contact support for further assistance.
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          onClick={() => onClose()}
        >
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default function UserProfile(props) {
  const [isLoading, setLoading] = useState(true);
  const [validations, setValidations] = useState({});
  const [saveWasSuccessful, setSaveWasSuccessful] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [verificationAttributeName, setVerificationAttributeName] = useState(null);
  const [locations, setLocation] = useState({});
  const [user, setUser] = useState(props.user);
  const [phoneDisplay, setPhoneDisplay] = useState(mapPhoneNumberToDisplay(user.phone_number));
  const { userAttributes, updateUserAttributes, syncUserAttributesUpdates } = useUserAttributes();
  const onCognitoAttributeChanged = (e) => {
    const { target: { id: key, value } } = e;
    setUser((previous) => ({
      ...previous,
      [key]: value,
    }));
  };
  const onUserAttributeChanged = (e) => {
    const { target: { id: key, value } } = e;
    updateUserAttributes({
      [key]: value,
    });
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const { locations: clientLocations } = await getClientLocations('locations');
        setLocation(clientLocations);
      } catch (error) {
        console.error('Error retrieving client locations:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Fetch tenant user attributes if needed
  useEffect(() => {
    async function fetchTenantAttributes() {
      /*
       * SHORT-TERM WORKAROUND: Fetch phone number and email from tenant pool when missing from main pool
       *
       * PROBLEM: Phone numbers and emails exist in tenant pools but aren't being mapped to main pools
       * during federated authentication via Cognito identity providers.
       *
       * LONG-TERM FIX SUGGESTIONS:
       * 1. Fix Cognito Identity Provider Attribute Mapping:
       *    - Verify tenant pool app client has 'phone' and 'email' in allowed OAuth scopes
       *    - Ensure identity provider attribute mapping includes: phone_number -> phone_number, email -> email
       *    - Check that tenant pool schema includes phone_number and email as standard attributes
       *    - Test that phone and email scopes are included in authorize_scopes (but this caused redirect loops)
       *
       * 2. Debug OIDC Claims:
       *    - Check what claims are actually sent from tenant pool to main pool
       *    - Verify phone_number and email are included in the ID token from tenant pool
       *    - Use CloudWatch logs in PreTokenGeneration Lambda to see what attributes are available
       *
       * 3. Alternative Solutions:
       *    - Use custom attributes (custom:phone_number, custom:email) if standard attribute mapping continues to fail
       *    - Implement a sync Lambda that runs on login to copy missing attributes
       *    - Use Cognito Pre-Authentication trigger to fetch and set missing attributes
       *
       * 4. Investigate Cognito Limitations:
       *    - Some standard attributes may not map correctly in federated scenarios
       *    - Phone/email verification status might need special handling
       *    - Consider if this is a known AWS Cognito limitation with OIDC providers
       *
       * 5. MFA Management for Tenant Users:
       *    - Currently hiding MFA controls for tenant users (controlled at pool level)
       *    - Long-term: Fetch tenant pool MFA configuration and display read-only status
       *    - Consider adding getTenantPoolConfig API to check if MFA is required/optional
       *    - Show appropriate messaging: "MFA managed by your organization" or similar
       *    - Handle cases where tenant pool requires MFA but main pool doesn't have it enabled
       *
       * EDGE CASES TO TEST:
       * - Users with phone numbers/emails in both pools (ensure no conflicts)
       * - Users without phone numbers/emails in either pool
       * - Phone number format differences between pools
       * - Verification status mismatches between pools
       * - Error handling when tenant pool is unreachable
       * - Performance impact of additional API calls
       * - Tenant users with different MFA requirements between pools
       * - Email format differences or case sensitivity issues
       */

      // If user is a tenant user, fetch all attributes from tenant pool to fill any gaps
      const userInfo = cognitoUser(user);

      if (userInfo.isTenantUser) {
        console.log('UserProfile: Fetching tenant user attributes for tenant user');
        try {
          const tenantResponse = await userService.getTenantUserAttributes();
          console.log('UserProfile: Tenant response received', tenantResponse);

          // Parse the response body if it's a string
          let attributes;
          if (tenantResponse.body && typeof tenantResponse.body === 'string') {
            const parsedBody = JSON.parse(tenantResponse.body);
            attributes = parsedBody.attributes;
          } else if (tenantResponse.attributes) {
            attributes = tenantResponse.attributes;
          }

          console.log('UserProfile: Parsed attributes', attributes);

          if (attributes) {
            const updates = {};

            // Handle phone number fallback (only if missing in main pool)
            if (!user.phone_number && attributes.phone_number) {
              console.log('UserProfile: Setting phone number from tenant pool', attributes.phone_number);
              updates.phone_number = attributes.phone_number;
              updates.phone_number_verified = attributes.phone_number_verified === 'true';
              setPhoneDisplay(mapPhoneNumberToDisplay(attributes.phone_number));
            }

            // Handle email fallback (only if missing in main pool)
            if (!user.email && attributes.email) {
              console.log('UserProfile: Setting email from tenant pool');
              updates.email = attributes.email;
              updates.email_verified = attributes.email_verified === 'true';
            }

            // Handle first name fallback (only if missing in main pool)
            if (!user.given_name && attributes.given_name) {
              console.log('UserProfile: Setting first name from tenant pool');
              updates.given_name = attributes.given_name;
            }

            // Handle last name fallback (only if missing in main pool)
            if (!user.family_name && attributes.family_name) {
              console.log('UserProfile: Setting last name from tenant pool');
              updates.family_name = attributes.family_name;
            }

            // Apply all updates at once if any were found
            if (Object.keys(updates).length > 0) {
              console.log('UserProfile: Applying tenant pool attribute updates', updates);
              setUser((previous) => ({
                ...previous,
                ...updates,
              }));
            } else {
              console.log('UserProfile: No missing attributes found in tenant pool');
            }
          } else {
            console.log('UserProfile: No attributes found in tenant pool response');
          }
        } catch (error) {
          console.error('UserProfile: Error retrieving tenant user attributes:', error);
        }
      }
    }

    if (user && Object.keys(user).length > 0) {
      fetchTenantAttributes();
    }
  }, [user]);

  useEffect(() => {
    setUser((previous) => ({
      ...previous,
      phone_number: mapPhoneNumberFromDisplay(phoneDisplay),
    }));
  }, [setUser, phoneDisplay]);

  const onVerify = (attribute) => async () => {
    const success = await onSave(
      () => null,
      setSaveFailed,
      setValidations,
      user,
      syncUserAttributesUpdates,
      setLoading,
    );
    if (!success) return;
    setVerificationAttributeName(attribute);
  };

  if (isLoading) {
    return (
      <Modal isOpen>
        <ModalHeader>User Profile</ModalHeader>
        <ModalBody className="d-flex justify-content-center">
          <Spinner />
        </ModalBody>
      </Modal>
    );
  }

  if (saveWasSuccessful) {
    return (
      <SuccessModal onClose={props.onClose} />
    );
  }

  return (
    <Modal isOpen>
      <ModalHeader>User Profile</ModalHeader>
      {verificationAttributeName && (
        <SendVerificationModal
          modalOpen
          toggle={() => setVerificationAttributeName(null)}
          onComplete={() => {
            setUser((previous) => ({
              ...previous,
              [`${verificationAttributeName}_verified`]: true,
            }));
            setVerificationAttributeName(null);
            props.onComplete();
          }}
          userAccount={user}
          attributeName={verificationAttributeName}
        />
      )}
      { saveFailed && <ErrorModal onClose={() => setSaveFailed(false)} />}
      <ModalBody>
        <FormGroup className="changeName" key="given_name">
          <Label for="firstName">First Name</Label>
          <SanitizedTextInput
            id="given_name"
            name="firstName"
            type="text"
            value={user.given_name}
            onChange={onCognitoAttributeChanged}
          />
          {renderValidationError(validations.firstName)}
        </FormGroup>
        <FormGroup key="family_name">
          <Label for="family_name">Last Name</Label>
          <SanitizedTextInput
            id="family_name"
            name="lastName"
            type="text"
            value={user.family_name}
            onChange={onCognitoAttributeChanged}
          />
          {renderValidationError(validations.lastName)}
        </FormGroup>
        <VerifiableFormGroup
          label={
            <Label for="setEmail" className="float-left">Email </Label>
              }
          inputProps={{
            id: 'setEmail',
            name: 'email',
            readOnly: true,
            type: 'email',
            value: user.email,
          }}
          isVerified={user.email_verified}
          onVerify={onVerify('email')}
        />
        <VerifiableFormGroup
          label={(
            <div>
              <Label for="phoneNum" className="float-left">Phone Number</Label>
              <FormText className="float-left ml-2">###-###-####</FormText>
            </div>
              )}
          inputProps={{
            id: 'phoneNum',
            name: 'phoneNum',
            type: 'tel',
            pattern: '[0-9]{3}-[0-9]{3}-[0-9]{4}',
            onChange: (event) => {
              setPhoneDisplay(event.target.value);
            },
            value: phoneDisplay,
          }}
          isVerified={user.phone_number_verified}
          onVerify={onVerify('phone_number')}
          validationError={renderValidationError(validations.phoneNumber)}
        />
        {/* Hide MFA options for tenant users - MFA is controlled at the tenant pool level */}
        {!cognitoUser(user).isTenantUser && (
          <FormGroup>
            <div className="d-flex justify-content-between">
              MFA Options
            </div>
            <MFACheckBox
              enabled={user.mfaPreferences.SMSMfaSettings?.Enabled}
              toggleDisabled={!user.phone_number_verified && !user.mfaPreferences.SMSMfaSettings?.Enabled}
              disabledReason="Verified phone number required"
              toggle={() => {
                setUser((previous) => {
                  const mfaEnabled = previous.mfaPreferences.SMSMfaSettings?.Enabled;
                  return ({
                    ...previous,
                    mfaPreferences: {
                      ...previous.mfaPreferences,
                      SMSMfaSettings: {
                        ...previous.mfaPreferences.SMSMfaSettings,
                        Enabled: !mfaEnabled,
                      },
                    },
                  });
                });
              }}
              verificationType="SMS"
            />
          </FormGroup>
        )}
        <FormGroup key="custom:homeLocationId" disabled>
          <LocationSelector
            disabled
            labelName="Home Location"
            id="homeLocationId"
            addresses={locations}
            attributeName="homeLocationId"
            onChange={onUserAttributeChanged}
            readOnly
            selectedLocationId={userAttributes.homeLocationId}
          />
        </FormGroup>
        { props.enabledFeatures.manageLocations ? (
          <FormGroup key="custom:preferredLocationId">
            <LocationSelector
              labelName="Preferred Location"
              id="preferredLocationId"
              addresses={locations}
              attributeName="preferredLocationId"
              onChange={onUserAttributeChanged}
              selectedLocationId={userAttributes.preferredLocationId || ''}
            />
          </FormGroup>
        ) : null}
        <FormGroup>
          <Label for="roles">User Roles</Label>
          <div className="d-flex flex-wrap p-2">
            {cognitoUser(user).consoleRoles
              .map((name) => (
                <span key={name} className={styles.groups} data-tooltip={name}>
                  {initialsFromCamelCase(name)}
                </span>
              ))}
          </div>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button
          id="save"
          color="primary"
          onClick={() => onSave(
            setSaveWasSuccessful,
            setSaveFailed,
            setValidations,
            user,
            syncUserAttributesUpdates,
            setLoading,
          )}
          disabled={isLoading}
          outline={isLoading}
        >
          {isLoading ? (<Spinner className="d-flex" size="sm" color="primary" />) : 'Save'}
        </Button>
              &nbsp;
        <Button
          id="cancel"
          color="secondary"
          onClick={() => props.onClose()}
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}
